import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Close, Refresh, Search } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Sync from "@mui/icons-material/Sync";
import {
  Box,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { summarizeKnowledgeBases } from "../../data/api/botpressKnowledgeBaseSync.js";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import {
  botpressBotODMSelector,
  knowledgeBaseUrlsSelector,
  testBotODMSelector,
} from "../../state/redux/botpressSession";
import {
  mrtGetActionColumnStyling,
  mrtGetActionIconSx,
  mrtGetBodyRowSx,
  mrtGetClampTextSx,
  mrtGetDateColumnSizing,
} from "../../utils/helpers.js";
import BotpressKnowledgeBaseSyncDialog from "../dialogs/botpressKnowledgeBaseSyncDialog.js";
import MRTDateTimeCell from "../ui/mrtDateTimeCell.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import DatagridTooltipProvider from "../ui/datagridTooltipProvider.js";
import Widget from "../ui/widget.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const MAX_PAGES_TO_DISPLAY = 200;
const MAX_NESTED_SITEMAPS_TO_LOAD = 10;
const MAX_NESTED_SITEMAP_DEPTH = 3;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const BotpressSiteMap = props => {
  const { widgetSize = "large", minHeight = "333px" } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();

  const organizationODM = useSelector(state => state.userSession.organizationODM);
  const knowledgeBaseUrls = useSelector(knowledgeBaseUrlsSelector);
  const botpressBotODM = useSelector(botpressBotODMSelector);
  const testBotODM = useSelector(testBotODMSelector);

  const [showKbSync, setShowKbSync] = useState(false);
  // Whether the Production bot has any knowledge-base content to copy. `null`
  // while the check is still running — the button stays enabled in that window
  // so we never briefly block a valid sync.
  const [hasSourceKbContent, setHasSourceKbContent] = useState(null);

  // For legacy orgs that never configured a separate Test bot, testBotODM falls
  // back to the PROD bot — the two ODMs are the same record. Syncing a bot onto
  // itself is meaningless, so the Knowledge Base Sync action is disabled.
  const isSameBot = botpressBotODM?.id != null && botpressBotODM.id === testBotODM?.id;

  // Decide whether there's anything to sync by listing the PROD bot's indexed
  // files and reusing the sync module's own definition of a knowledge-base file
  // (the same one the dialog uses). This is accurate for every content type —
  // markdown, scraped HTML, PDFs — where a redux-only check would miss non-
  // markdown files, and it only reads file metadata (no content downloads).
  useEffect(() => {
    if (!botpressBotODM) {
      setHasSourceKbContent(false);
      return undefined;
    }

    let cancelled = false;
    setHasSourceKbContent(null);

    BotpressAPI.listAllBotpressFiles({ botODM: botpressBotODM, indexed: true })
      .then(files => {
        if (cancelled) {
          return;
        }
        setHasSourceKbContent(summarizeKnowledgeBases(files).length > 0);
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        // Don't wrongly disable the button if the check fails — let the user open
        // the dialog, which does its own precise loading and error handling.
        console.error("Failed to check the Production knowledge base:", error);
        setHasSourceKbContent(true);
      });

    return () => {
      cancelled = true;
    };
  }, [botpressBotODM]);

  const hasNothingToSync = hasSourceKbContent === false;

  const syncDisabled = isSameBot || hasNothingToSync;

  const publicWebsite =
    organizationODM?.omniData?.publicWebsite || organizationODM?.publicWebsite || "";

  const [siteMapRows, setSiteMapRows] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [sorting, setSorting] = useState([]);

  // Initialize with the "scraped" pages hidden. Seeding columnFilters here (as
  // opposed to a first-render effect) means the "Clear All Filters" button is
  // enabled the moment the table loads.
  const [columnFilters, setColumnFilters] = useState([
    {
      id: "knowledgeBaseStatus",
      value: "Not Scraped",
    },
  ]);
  const [expanded, setExpanded] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");

  const [columnOrder, setColumnOrder] = useState([
    "mrt-row-expand",
    "actions",
    "lastModified",
    "status",
    "knowledgeBaseStatus",
    "title",
    "displayUrl",
  ]);

  const widgetTitleText = publicWebsite
    ? `${pageCount} Pages`
    : "No public website set for this organization";

  const normalizeUrl = useCallback(url => {
    if (!url) {
      return "";
    }

    try {
      const parsedUrl = new URL(url);
      parsedUrl.hash = "";
      parsedUrl.search = "";

      let normalizedUrl = parsedUrl.href.toLowerCase();

      if (normalizedUrl.endsWith("/")) {
        normalizedUrl = normalizedUrl.slice(0, -1);
      }

      return normalizedUrl;
    } catch (error) {
      return url.toLowerCase().replace(/\/$/, "");
    }
  }, []);

  const addKnowledgeBaseStatusToRows = useCallback(
    rows => {
      // Each scraped-website record exposes an array of URLs on `scrapedUrls`
      // (not a single `url` field), so flatten those out before normalizing.
      const knowledgeBaseUrlSet = new Set(
        (knowledgeBaseUrls || [])
          .flatMap(record => record.scrapedUrls || [])
          .map(url => normalizeUrl(url))
          .filter(url => url != null && url !== ""),
      );

      return rows.map(row => {
        const normalizedRowUrl = normalizeUrl(row.url);
        const isInKnowledgeBase = knowledgeBaseUrlSet.has(normalizedRowUrl);

        return {
          ...row,
          knowledgeBaseStatus: isInKnowledgeBase ? "Scraped" : "Not Scraped",
          subRows:
            row.subRows?.length > 0 ? addKnowledgeBaseStatusToRows(row.subRows) : row.subRows,
        };
      });
    },
    [knowledgeBaseUrls, normalizeUrl],
  );

  const siteMapRowsWithKnowledgeBaseStatus = useMemo(() => {
    return addKnowledgeBaseStatusToRows(siteMapRows);
  }, [addKnowledgeBaseStatusToRows, siteMapRows]);

  const getProxyUrl = useCallback(targetUrl => {
    return `/sitemap-proxy?target=${encodeURIComponent(targetUrl)}`;
  }, []);

  const getSiteMapUrls = useCallback(websiteUrl => {
    const url = new URL(
      /^https?:\/\//i.test(websiteUrl) ? websiteUrl : `https://${websiteUrl}`,
    );

    if (url.pathname.endsWith(".xml")) {
      return [url.href];
    }

    const origins = [url.origin];

    if (url.hostname.startsWith("www.")) {
      origins.push(`${url.protocol}//${url.hostname.replace("www.", "")}`);
    } else {
      origins.push(`${url.protocol}//www.${url.hostname}`);
    }

    const siteMapUrls = origins.flatMap(origin => [
      `${origin}/sitemap.xml`,
      `${origin}/sitemap_index.xml`,
      `${origin}/wp-sitemap.xml`,
    ]);

    return Array.from(new Set(siteMapUrls));
  }, []);

  const getPageTitle = useCallback(pageUrl => {
    try {
      const url = new URL(pageUrl);
      const pathParts = url.pathname
        .split("/")
        .filter(pathPart => pathPart)
        .map(pathPart => pathPart.replace(/-/g, " ").replace(/\b\w/g, char => char.toUpperCase()));

      if (pathParts.length === 0) {
        return "Home";
      }

      return pathParts[pathParts.length - 1];
    } catch (error) {
      return pageUrl;
    }
  }, []);

  const getSiteMapTitle = useCallback(siteMapUrl => {
    try {
      const url = new URL(siteMapUrl);
      const pathParts = url.pathname.split("/").filter(pathPart => pathPart);

      return pathParts[pathParts.length - 1] || siteMapUrl;
    } catch (error) {
      return siteMapUrl;
    }
  }, []);

  const getDisplayUrl = useCallback(pageUrl => {
    try {
      const url = new URL(pageUrl);

      return url.href;
    } catch (error) {
      return pageUrl;
    }
  }, []);

  const getStatusSx = useCallback(
    status => {
      switch (status) {
        case "Sitemap":
          return {
            backgroundColor: alpha(theme.palette.commonground.contrastText, 0.4),
            color: theme.palette.commonground.contrastText,
          };
        case "Up To Date":
          return {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
          };
        case "Needs Update":
          return {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          };
        case "New":
        default:
          return {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
          };
      }
    },
    [theme],
  );

  const getKnowledgeBaseStatusSx = useCallback(
    status => {
      if (status === "Scraped") {
        return {
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
        };
      }

      return {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
      };
    },
    [theme],
  );

  const parseSiteMapXml = useCallback(
    xmlText => {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, "application/xml");

      const parserError = xmlDoc.querySelector("parsererror");

      if (parserError) {
        throw new Error("Unable to parse sitemap.xml");
      }

      const nestedSiteMaps = Array.from(xmlDoc.getElementsByTagName("sitemap")).flatMap(
        (siteMapNode, siteMapIndex) => {
          const lastModified = siteMapNode.getElementsByTagName("lastmod")[0]?.textContent || "";

          return Array.from(siteMapNode.getElementsByTagName("loc"))
            .map((locNode, locIndex) => {
              const loc = locNode?.textContent || "";

              return {
                id: loc || `sitemap-${siteMapIndex}-${locIndex}`,
                lastModified,
                loc,
              };
            })
            .filter(siteMap => siteMap.loc);
        },
      );

      const pageUrls = Array.from(xmlDoc.getElementsByTagName("url")).flatMap(
        (urlNode, urlIndex) => {
          const lastModified = urlNode.getElementsByTagName("lastmod")[0]?.textContent || "";

          return Array.from(urlNode.getElementsByTagName("loc"))
            .map((locNode, locIndex) => {
              const loc = locNode?.textContent || "";

              return {
                id: loc || `page-${urlIndex}-${locIndex}`,
                actions: "Open",
                lastModified,
                status: "New",
                title: getPageTitle(loc),
                url: loc,
                displayUrl: getDisplayUrl(loc),
              };
            })
            .filter(page => page.url);
        },
      );

      return {
        nestedSiteMaps,
        pageUrls,
      };
    },
    [getDisplayUrl, getPageTitle],
  );

  const loadNestedSiteMaps = useCallback(
    async (nestedSiteMaps, depth = 0) => {
      const limitedNestedSiteMaps = nestedSiteMaps.slice(0, MAX_NESTED_SITEMAPS_TO_LOAD);

      const nestedRows = await Promise.all(
        limitedNestedSiteMaps.map(async siteMap => {
          const fallbackRow = {
            id: siteMap.loc,
            actions: "Open",
            lastModified: siteMap.lastModified,
            status: "Sitemap",
            title: getSiteMapTitle(siteMap.loc),
            url: siteMap.loc,
            displayUrl: getDisplayUrl(siteMap.loc),
            subRows: [],
          };

          try {
            const response = await fetch(getProxyUrl(siteMap.loc));
            const responseLastModified = response.headers.get("last-modified") || "";
            const rowLastModified = siteMap.lastModified || responseLastModified;

            if (!response.ok) {
              return {
                ...fallbackRow,
                lastModified: rowLastModified,
              };
            }

            const xmlText = await response.text();
            const parsedSiteMap = parseSiteMapXml(xmlText);

            let subRows = parsedSiteMap.pageUrls.slice(0, MAX_PAGES_TO_DISPLAY).map(pageRow => ({
              ...pageRow,
              lastModified: pageRow.lastModified || responseLastModified,
            }));

            if (
              subRows.length === 0 &&
              parsedSiteMap.nestedSiteMaps.length > 0 &&
              depth < MAX_NESTED_SITEMAP_DEPTH
            ) {
              subRows = await loadNestedSiteMaps(
                parsedSiteMap.nestedSiteMaps.map(nestedSiteMap => ({
                  ...nestedSiteMap,
                  lastModified: nestedSiteMap.lastModified || responseLastModified,
                })),
                depth + 1,
              );
            }

            return {
              ...fallbackRow,
              lastModified: rowLastModified,
              subRows,
            };
          } catch (error) {
            console.error("Unable to load nested sitemap", siteMap.loc, error);

            return fallbackRow;
          }
        }),
      );

      return nestedRows;
    },
    [getDisplayUrl, getProxyUrl, getSiteMapTitle, parseSiteMapXml],
  );

  const flattenSiteMapRows = useCallback(rows => {
    return rows.flatMap(row => {
      if (row.subRows?.length > 0) {
        return flattenSiteMapRows(row.subRows);
      }

      return row.status === "Sitemap" ? [] : [row];
    });
  }, []);

  const getTotalPageCount = useCallback(rows => {
    return rows.reduce((total, row) => {
      if (row.subRows?.length > 0) {
        return total + getTotalPageCount(row.subRows);
      }

      return row.status === "Sitemap" ? total : total + 1;
    }, 0);
  }, []);

  const loadSiteMap = useCallback(async () => {
    setErrorMessage("");
    setSiteMapRows([]);
    setPageCount(0);
    setExpanded({});
    setIsLoading(true);
    setIsRefreshSpinning(true);

    if (!publicWebsite) {
      setIsLoading(false);
      setIsRefreshSpinning(false);
      return;
    }

    try {
      const siteMapUrls = getSiteMapUrls(publicWebsite);

      let parsedSiteMap = null;

      for (const siteMapUrl of siteMapUrls) {
        try {
          const response = await fetch(getProxyUrl(siteMapUrl));

          if (!response.ok) {
            continue;
          }

          const xmlText = await response.text();
          const parsedCandidate = parseSiteMapXml(xmlText);

          if (parsedCandidate.pageUrls.length > 0 || parsedCandidate.nestedSiteMaps.length > 0) {
            parsedSiteMap = parsedCandidate;
            break;
          }
        } catch (error) {
          console.error("Unable to load sitemap candidate", siteMapUrl, error);
        }
      }

      if (parsedSiteMap == null) {
        throw new Error("Unable to load sitemap.");
      }

      let rows = parsedSiteMap.pageUrls.slice(0, MAX_PAGES_TO_DISPLAY);

      if (parsedSiteMap.nestedSiteMaps.length > 0) {
        rows = await loadNestedSiteMaps(parsedSiteMap.nestedSiteMaps);
      }

      const totalPageCount = getTotalPageCount(rows);

      setSiteMapRows(rows);
      setPageCount(totalPageCount);

      if (totalPageCount === 0) {
        setErrorMessage("No pages were found in this sitemap.");
      }
    } catch (error) {
      setErrorMessage(
        "Unable to load the sitemap for this website. The site may not have a sitemap.xml file, or it may block browser requests.",
      );
    } finally {
      setIsLoading(false);

      setTimeout(() => {
        setIsRefreshSpinning(false);
      }, 800);
    }
  }, [
    getProxyUrl,
    getSiteMapUrls,
    getTotalPageCount,
    loadNestedSiteMaps,
    parseSiteMapXml,
    publicWebsite,
  ]);

  useEffect(() => {
    loadSiteMap();
  }, [loadSiteMap]);

  const columns = useMemo(
    () => [
      {
        ...mrtGetActionColumnStyling(),
        accessorKey: "actions",
        header: "Open",
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        enableSorting: false,
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            padding: "4px 6px",
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          },
        },
        Cell: ({ row }) => (
          <Tooltip title="Open page in a new window" arrow>
            <IconButton
              onClick={() => {
                window.open(row.original.url, "_blank");
              }}
              sx={{ color: theme.palette.secondary.main, ...mrtGetActionIconSx(theme) }}
            >
              <OpenInNew />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        accessorKey: "lastModified",
        header: "Date",
        ...mrtGetDateColumnSizing(),
        enableColumnFilterModes: false,
        enableSorting: true,
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: MRTDateTimeCell,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        minSize: 160,
        grow: false,
        enableColumnFilterModes: false,
        enableSorting: true,
        filterFn: "equals",
        filterSelectOptions: [
          { label: "Sitemap", value: "Sitemap" },
          { label: "New", value: "New" },
          { label: "Up To Date", value: "Up To Date" },
          { label: "Needs Update", value: "Needs Update" },
        ],
        filterVariant: "select",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ cell }) => (
          <Box
            component="span"
            sx={{
              ...getStatusSx(cell.getValue()),
              borderRadius: "0.25rem",
              display: "inline-block",
              minWidth: "110px",
              padding: theme.spacing(1),
            }}
          >
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              {cell.getValue()}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "knowledgeBaseStatus",
        header: "KB Status",
        size: 130,
        minSize: 130,
        grow: false,
        enableColumnFilterModes: false,
        enableSorting: true,
        filterFn: "equals",
        filterSelectOptions: [
          { label: "Scraped", value: "Scraped" },
          { label: "Not Scraped", value: "Not Scraped" },
        ],
        filterVariant: "select",
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ cell }) => (
          <Box
            component="span"
            sx={{
              ...getKnowledgeBaseStatusSx(cell.getValue()),
              borderRadius: "0.25rem",
              display: "inline-block",
              minWidth: "95px",
              padding: theme.spacing(1),
            }}
          >
            <Typography variant="button" sx={{ fontWeight: "bold" }}>
              {cell.getValue() || "Not Scraped"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 300,
        minSize: 300,
        grow: false,
        enableColumnFilterModes: false,
        enableSorting: false,
        enableColumnFilter: true,
        filterVariant: "text",
        filterFn: "contains",
        muiTableHeadCellProps: {
          align: "left",
        },
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: "normal",
            wordWrap: "break-word",
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ cell, row }) => (
          <Typography
            variant="subtitle1"
            sx={{
              ...mrtGetClampTextSx(),
              textAlign: "left",
              fontSize: row.original.status === "Sitemap" ? "0.82rem" : "0.78rem",
              fontWeight: row.original.status === "Sitemap" ? "bold" : "normal",
              lineHeight: 1.1,
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "displayUrl",
        header: "URL",
        size: 900,
        minSize: 450,
        grow: true,
        enableColumnFilterModes: false,
        enableSorting: false,
        enableColumnFilter: true,
        filterVariant: "text",
        filterFn: "contains",
        muiTableHeadCellProps: {
          align: "left",
        },
        muiTableBodyCellProps: {
          sx: {
            whiteSpace: "normal",
            wordWrap: "break-word",
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            padding: "4px 3px",
          },
        },
        Cell: ({ row }) => (
          <Link
            href={row.original.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              ...mrtGetClampTextSx(),
              fontSize: "0.76rem",
              lineHeight: 1.1,
              fontWeight: row.original.status === "Sitemap" ? "bold" : "normal",
            }}
          >
            {row.original.displayUrl}
          </Link>
        ),
      },
    ],
    [getKnowledgeBaseStatusSx, getStatusSx, theme],
  );

  // Memoize the table data. When a global filter is active we flatten the tree,
  // but doing that inline produced a brand-new array reference on every render,
  // which sent Material React Table into an infinite re-render loop (the table
  // locked up as soon as you typed). Keeping a stable reference fixes it.
  const tableData = useMemo(
    () =>
      globalFilter
        ? flattenSiteMapRows(siteMapRowsWithKnowledgeBaseStatus)
        : siteMapRowsWithKnowledgeBaseStatus,
    [globalFilter, flattenSiteMapRows, siteMapRowsWithKnowledgeBaseStatus],
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnResizing: false,
    enableExpanding: !globalFilter,
    enableExpandAll: false,
    enableGlobalFilter: true,
    filterFromLeafRows: true,
    maxLeafRowFilterDepth: 1,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: false,
    enableRowSelection: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: true,
    enableHiding: true,
    enablePagination: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    paginateExpandedRows: false,
    layoutMode: "grid",
    initialState: {
      density: "compact",
      columnVisibility: {
        actions: true,
      },
      pagination: {
        pageIndex: 0,
        pageSize: 50,
      },
    },
    defaultColumn: {
      minSize: 60,
      maxSize: 220,
      size: 100,
      grow: false,
    },
    state: {
      columnFilters,
      columnOrder,
      expanded,
      globalFilter,
      isLoading,
      sorting,
    },
    getRowId: row => row.id,
    getSubRows: row => (globalFilter ? undefined : row.subRows),
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    onColumnOrderChange: setColumnOrder,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    paginationDisplayMode: "pages",
    enableFirstPageButton: false,
    enableLastPageButton: false,
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },
    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "transparent !important",
        boxShadow: "none",
        color: theme.palette.commonground.contrastText,

        "&.MuiAlert-root": {
          backgroundColor: "transparent !important",
          boxShadow: "none",
        },

        "& .MuiAlert-message": {
          color: theme.palette.commonground.contrastText,
        },

        "& .MuiAlert-action": {
          color: theme.palette.secondary.main,
        },

        "& button": {
          color: `${theme.palette.secondary.main} !important`,
        },

        "& .MuiButton-root": {
          color: `${theme.palette.secondary.main} !important`,
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        flexShrink: 0,
        minHeight: "48px",
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiInputAdornment-root .MuiIconButton-root": {
          border: "none !important",
          backgroundColor: "transparent !important",
          marginLeft: "0px !important",
          color: `${theme.palette.secondary.main} !important`,
          padding: "4px !important",
          borderRadius: "50%",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:hover": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.2) !important",
        },
        "& .MuiInputAdornment-root .MuiIconButton-root:active": {
          border: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.3) !important",
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
        flexShrink: 0,
        height: "56px",
        minHeight: "56px",
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: mobileLayout
        ? {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            flex: 1,
            minHeight: 0,
            maxHeight: "none",
            overflowX: "auto",
            overflowY: "auto",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
        tableLayout: "fixed",
        width: "100%",
      },
    },
    muiTableBodyRowProps: {
      sx: mrtGetBodyRowSx(),
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: alpha(theme.palette.commonground.contrastText, 0.12),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
        fontWeight: "bold",
        fontSize: "0.72rem",
        padding: "3px 3px",
        whiteSpace: "nowrap",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        size: 35,
        grow: false,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            color: theme.palette.secondary.main,
            padding: "4px 2px",
          },
        },
      },
    },
    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: "48px",
          paddingLeft: theme.spacing(1),
        }}
      >
        <TextField
          value={globalFilter ?? ""}
          onChange={event => {
            setGlobalFilter(event.target.value);
          }}
          placeholder="Search site map"
          size="small"
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  onClick={() => {
                    setGlobalFilter("");
                  }}
                  edge="end"
                  size="small"
                  sx={{
                    opacity: globalFilter == null || globalFilter === "" ? 0.45 : 1,
                    border: "none !important",
                    backgroundColor: "transparent !important",
                    color: `${theme.palette.secondary.main} !important`,
                    marginLeft: "0px !important",
                    padding: "4px !important",
                    borderRadius: "50%",
                    "&:hover": {
                      border: "none !important",
                      backgroundColor: "rgba(0, 0, 0, 0.2) !important",
                    },
                    "&:active": {
                      border: "none !important",
                      backgroundColor: "rgba(0, 0, 0, 0.3) !important",
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: "300px",
            marginLeft: theme.spacing(1),
            backgroundColor: theme.palette.commonground.main,
            "& .MuiInputBase-root": {
              height: "40px",
              color: theme.palette.commonground.contrastText,
            },
            "& .MuiInputAdornment-root": {
              color: theme.palette.commonground.contrastText,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(theme.palette.commonground.contrastText, 0.1),
            },
            "& .MuiInputAdornment-root .MuiIconButton-root": {
              border: "none !important",
              backgroundColor: "transparent !important",
              color: `${theme.palette.secondary.main} !important`,
              marginLeft: "0px !important",
              padding: "4px !important",
              borderRadius: "50%",
            },
            "& .MuiInputAdornment-root .MuiIconButton-root:hover": {
              border: "none !important",
              backgroundColor: "rgba(0, 0, 0, 0.2) !important",
            },
            "& .MuiInputAdornment-root .MuiIconButton-root:active": {
              border: "none !important",
              backgroundColor: "rgba(0, 0, 0, 0.3) !important",
            },
          }}
        />
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setSorting([]);
              }}
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}

        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Refresh site map" arrow>
          <IconButton onClick={loadSiteMap} disabled={isLoading || !publicWebsite}>
            <Refresh
              sx={{
                animation: isRefreshSpinning ? `${spin} 0.8s linear` : "none",
              }}
            />
          </IconButton>
        </Tooltip>

        {/* eslint-disable react/jsx-pascal-case */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enable react/jsx-pascal-case */}
      </Box>
    ),
    renderEmptyRowsFallback: () => (
      <Typography sx={{ padding: theme.spacing(2) }}>No sitemap pages found.</Typography>
    ),
  });

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={`Site Map - ${publicWebsite ? publicWebsite.replace(/^https?:\/\/(www\.)?/i, "") : "None"}`}
        text={errorMessage ? errorMessage : widgetTitleText}
      >
        <OmniResponsiveButton
          tooltipTitle={
            isSameBot
              ? "A separate Test bot is required to sync the Knowledge Base"
              : hasNothingToSync
                ? "The Production bot has no Knowledge Base files to sync"
                : "Copy the Knowledge Base from Production into the Test bot (Prod → Test)"
          }
          buttonText="Sync KB"
          icon={<Sync />}
          disabled={syncDisabled}
          onClick={() => {
            if (!botpressBotODM || !testBotODM) {
              enqueueSnackbar("Both a Production and a Test bot are required to sync.", {
                variant: "error",
              });
              return;
            }
            setShowKbSync(true);
          }}
        />
      </WidgetTitle>

      {showKbSync && (
        <BotpressKnowledgeBaseSyncDialog
          open={showKbSync}
          sourceBotODM={botpressBotODM}
          targetBotODM={testBotODM}
          onClose={() => setShowKbSync(false)}
        />
      )}

      <WidgetSurveyContent>
        <Box
          aria-disabled={!publicWebsite}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // No public website configured -> disable the table entirely.
            opacity: publicWebsite ? 1 : 0.45,
            pointerEvents: publicWebsite ? "auto" : "none",
            userSelect: publicWebsite ? "auto" : "none",
            transition: "opacity 0.25s ease",
          }}
        >
          <DatagridTooltipProvider>
            <MaterialReactTable table={table} />
          </DatagridTooltipProvider>
        </Box>
      </WidgetSurveyContent>
    </Widget>
  );
};

export default BotpressSiteMap;
